import { Component, input, output, signal } from '@angular/core';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzTableModule, NzTableSortFn, NzTableSortOrder } from 'ng-zorro-antd/table';
import { AngularModule } from '../../../../../modules/angular.module';
import { Course, Section } from '@mira-avant/mtlibs';
import { NzModalService } from 'ng-zorro-antd/modal';
import { SectionService } from '../../../../../services/section.service';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { LocationNamePipe } from '../../../../../pipes/location-name-pipe';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
	selector: 'app-section-table',
	standalone: true,
	imports: [
		AngularModule,
		NzTableModule,
		NzEmptyModule,
		NzPaginationModule,
		LocationNamePipe,
		NzPopconfirmModule,
		NzButtonModule,
		NzIconModule,
	],
	templateUrl: './section-table.html',
	styleUrl: './section-table.scss',
})
export class SectionTable {
	// Inputs → signals
	readonly editSection = input<Section | undefined>();
	readonly course = input<Course | undefined>();
	readonly clientUuid = input<string | undefined>();

	// Outputs → signal-based
	readonly onEditSection = output<Section | undefined>();
	readonly onDelete = output<Section>();

	readonly sections = signal<Section[]>([]);
	readonly sortOrder = signal<NzTableSortOrder>(null);
	readonly totalSections = signal<number>(0);
	readonly pageNumber = signal<number>(1);
	readonly loading = signal<boolean>(false);

	readonly SECTIONS_PER_PAGE = 2;

	constructor(
		private modal: NzModalService,
		private sectionService: SectionService
	) {}

	async ngOnChanges(): Promise<void> {
		// React to course input changes
		if (this.course()) {
			await this.reloadPage();
		}
	}

	public async loadPageOfSections(pageNumber: number): Promise<void> {
		this.loading.set(true);
		this.pageNumber.set(pageNumber);

		const courseUuid = this.course()?.uuid;
		if (!courseUuid) {
			throw new Error('No course selected');
		}

		try {
			const sections = await this.sectionService.getSectionsByCourse$(
				courseUuid,
				pageNumber - 1,
				this.SECTIONS_PER_PAGE
			);

			this.sections.set(sections);
		} catch (error) {
			console.error('Error loading sections:', error);
		} finally {
			this.loading.set(false);
		}
	}

	public async reloadPage(): Promise<void> {
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public async reset(): Promise<void> {
		this.pageNumber.set(1);
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public async updateTotals(): Promise<void> {
		const courseUuid = this.course()?.uuid;
		if (!courseUuid) {
			throw new Error('No course selected');
		}

		const total = await this.sectionService.getTotalSectionsByCourse(courseUuid);
		this.totalSections.set(total);
	}

	public async onPageIndexChange(pageNumber: number): Promise<void> {
		await this.loadPageOfSections(pageNumber);
	}

	public edit(section: Section | undefined): void {
		this.onEditSection.emit(section);
	}

	public async reloadCurrentPage(): Promise<void> {
		await this.loadPageOfSections(this.pageNumber());
		await this.updateTotals();
	}

	public sortNameFn: NzTableSortFn<Section> = (a, b) => {
		const nameA = a.name?.toLowerCase() || '';
		const nameB = b.name?.toLowerCase() || '';
		return nameA.localeCompare(nameB);
	};

	public onSortOrderChange(order: NzTableSortOrder): void {
		this.sortOrder.set(order);
	}

	public delete(section: Section): void {
		this.modal.confirm({
			nzTitle: 'Delete Section',
			nzContent: `Are you sure you want to delete the section "${section.name}"?`,
			nzOkText: 'Yes',
			nzOkType: 'primary',
			nzOkDanger: true,
			nzOnOk: () => this.onDelete.emit(section),
			nzCancelText: 'No',
		});
	}
}
